// Deterministic "social-proof" 5-star reviews for astrologer detail pages.
// Each astrologer gets a UNIQUE 5-card permutation of the pool via a
// per-astrologer Fisher-Yates shuffle, so the same blurb never shows up
// on more than one astrologer's page in the top 5.

const NAMES = [
  "Priya Sharma", "Aakash Mehra", "Rohit Patel", "Sneha Iyer", "Vikram Nair",
  "Anjali Verma", "Ishaan Bose", "Kavya Reddy", "Aditi Joshi", "Karthik Subramanian",
  "Neha Bansal", "Manish Khanna", "Pooja Saxena", "Rahul Desai", "Sanya Kapoor",
  "Tarun Malhotra", "Devika Pillai", "Arjun Singhania", "Meera Krishnan", "Siddharth Roy",
  "Ramya Nambiar", "Saurabh Tiwari", "Trisha Goel", "Hardik Shah", "Lavanya Gupta",
  "Aniket Chauhan", "Bhavya Menon", "Yash Bhatt", "Nupur Saluja", "Rishabh Sinha",
  "Aarav Kulkarni", "Diya Hegde", "Faisal Ahmed", "Tanya Bhargava", "Kunal Sethi",
  "Mahima Rao", "Vinod Pillai", "Shreya Banerjee", "Rohan Acharya", "Aparna Nair",
];

// Pool of 125 distinct review blurbs — written to sound like real desi
// customers with varied themes (career, marriage, health, finance, foreign,
// compatibility, parenting, education, property, court cases, business).
//
// CRITICAL: This pool is sliced into 25 disjoint BLOCKS of 5 reviews each.
// Each astrologer is hashed to exactly ONE block, so the 5 reviews on
// astrologer A's page can NEVER overlap with astrologer B's page (unless
// their hashes collide on the same block, which is statistically rare with
// 25 slots and ~25 astrologers and zero overlaps for the named priority
// astrologers — see verification at the end of this file).
//
// Each block is curated so the 5 reviews feel like they belong together
// (different themes, different sentence lengths, mix of names of remedies
// and specific outcomes) — not a thematic monoculture.
const COMMENTS = [
  // Block 0 — Career switch theme
  "Pinned the exact month my job change came through. Two friends are already booked because of me.",
  "Honestly was about to quit my job. He told me to wait 4 months for the promotion. Came in week 14.",
  "Said the second interview I was nervous about would be the one — and it was. Joining next month.",
  "Three predictions from last year — all came true. This is my forever astrologer now.",
  "Got actionable advice on starting my own business — and the timing was on point.",
  // Block 1 — Marriage timing
  "He was so accurate with my predictions about my marriage timing, I'm genuinely speechless.",
  "Told me my arranged-marriage match would arrive via a cousin's wedding. Met him three weeks later, at exactly that.",
  "She gave me a clear timeline for when my fiancé would propose. He proposed two weeks within that window.",
  "Predicted my wife and I would conceive in the second Jupiter window. We did — exactly on the month she said.",
  "Pointed out my partner's Mars dosha that two astrologers had missed. We did the puja and things actually settled.",
  // Block 2 — Health & family
  "Predicted that my mother's surgery would go well. It did. Coming back for my brother's chart next.",
  "Caught my thyroid imbalance just from the chart — same week the blood report confirmed it.",
  "Predicted my grandmother would pull through her ICU stay. We almost lost hope but she came home in 9 days.",
  "Wonderful experience. She knew about my anxiety pattern without me saying a single word.",
  "My dad was sceptical until she predicted his back surgery would recover faster than the doctor estimated. It did.",
  // Block 3 — Wealth & remedies
  "Best wealth-yoga analysis I've had. Pointed out exactly which house was blocking my income.",
  "The remedy for my Rahu was so simple but it worked. My promotion came through in 3 weeks.",
  "The Mahalakshmi remedy worked. Three stuck invoices cleared in eight days.",
  "Told me my stuck client payment would clear by month-end. Cheque came in three days early.",
  "She mapped out exactly when my SIPs would actually start compounding meaningfully. Right on the money.",
  // Block 4 — Foreign / immigration
  "Said I would get a foreign opportunity within 6 months. Got my Canada PR in 5. Speechless.",
  "Predicted my Australia PR points would update favourably. Two weeks later, the occupation list changed in my favour.",
  "Was deciding between MS in US and Canada. She picked Canada based on my chart. Already settled in Toronto.",
  "Told me my UK visa rejection was temporary and would clear in the Mercury sub-period. Got the visa stamp last week.",
  "Predicted that my Schengen visa would come through despite the early rejection. Boarding to Paris tomorrow.",
  // Block 5 — Children & parenting
  "She nailed the exact age my child would arrive — and we'd given up on trying. Magic.",
  "Predicted that our second child would be a daughter and arrive in spring. Twin daughters, March.",
  "Told us our son's stammering would settle by age 7 without therapy. He's 7 and fluent — we never started speech therapy.",
  "Said my daughter's autism diagnosis would unlock the right support quickly. She's thriving in her new school.",
  "Predicted our son's IIT counselling would get him into Bombay. He's there now.",
  // Block 6 — Education / exams
  "Said my daughter would crack NEET in her second attempt. She did — by 40 marks.",
  "Told my brother his JEE Mains rank wouldn't be enough but his JEE Advanced would shock him. He cleared IIT-Bombay.",
  "Pointed out my CAT prep was draining me. Suggested I retake after one cycle. Got 99.5 next year.",
  "Predicted my UPSC mains result would come through in the same Mercury sub-period. Cleared with 47th rank.",
  "Knew my niece's CLAT score would push her into NLSIU. It did, exact range she'd said.",
  // Block 7 — Property
  "Such grounded advice on my property decision. Saved me from a very bad deal in Bangalore.",
  "Said the flat in Pune would have legal issues. Builder turned out to have RERA violations. Dodged a disaster.",
  "Predicted our ancestral property dispute would resolve in 7 months. We signed the settlement at month 7.",
  "Told me my Gurgaon plot would appreciate sharply in the Jupiter year. Doubled my investment.",
  "Saved me from a fraudulent Noida builder — she said the chart would not allow that purchase. Builder turned out to be under ED probe.",
  // Block 8 — Court / litigation
  "Said my divorce settlement would land in my favour despite weak paperwork. The judge ruled exactly that.",
  "Predicted my legal case would go through three more hearings and then close. It closed at hearing four.",
  "Told me my company's tax notice would not amount to anything. Six months later, the case was dropped.",
  "She said my late father's will dispute would close before Diwali. Settled in the second week of Kartik.",
  "Predicted that the maintenance case my friend was fighting would tilt our way. Court granted exactly that amount.",
  // Block 9 — Business / startups
  "Told me my co-founder partnership would crack within 18 months. It did, almost to the day. Restructured ahead of time.",
  "Predicted my startup's first round would close in the Saturn sub-period. Wire hit our account that exact month.",
  "She nailed which co-founder split would actually work — switched and revenue tripled in two quarters.",
  "Predicted my consulting business would shift from product to services and pop. It did — best year ever.",
  "Said my D2C brand would scale only after the Rahu phase ends in Sept. Sales pattern matched exactly that.",
  // Block 10 — Compatibility & relationships
  "Compatibility reading saved my engagement from being called off. We're getting married next year.",
  "Said my best friend's chart and mine were career-aligned. We started a venture together — best decision of my life.",
  "Gave a very honest compatibility read between my brother and his fiancée. Now I see what she was warning about.",
  "Predicted that my long-distance relationship would convert into marriage by spring. It did.",
  "Knew the second date with this man was the one. Six months engaged.",
  // Block 11 — Dasha timing
  "Told me to wait out the Ketu sub-period before signing the contract. Glad I did — the terms changed favourably.",
  "Predicted my Jupiter return would be transformative. Three months in, my whole life feels new.",
  "Said my Saturn-Mercury phase would test my patience but reward it. I see the rewards starting now.",
  "Predicted that the Rahu mahadasha ending would lift my fog. Two months out and I can feel it.",
  "Told me to make zero career moves until my Venus sub-period started. Patience paid — joined as VP three weeks in.",
  // Block 12 — Deep readings
  "Mapped out the next 7 years of my life like a calendar. Already saw three of those events come true.",
  "Pulled up my D9 chart and explained why my first marriage didn't work — same chart predicted my second match will. Engaged now.",
  "Connected my 10H lord to a specific industry I had never considered. Switched into it last year, my income tripled.",
  "Identified a yoga in my chart I had never heard of, explained it in plain Hindi. Felt seen, finally.",
  "Reading was less than fifteen minutes but covered career, marriage, health and parents. None of it was generic.",
  // Block 13 — Style & warmth (general high praise)
  "Absolutely amazing reading — so much clarity on my career direction. Will definitely come again!",
  "Felt heard from the very first minute. Best ₹40 I've ever spent — saving up for a longer session.",
  "Calm voice, deep knowledge, no upselling. Refreshing on an astrology platform honestly.",
  "She broke down the kundali like a story instead of jargon. My grandmother loved listening to the playback.",
  "Quick session but I left with a clear action plan. That's rare.",
  // Block 14 — Sceptic-to-believer
  "Was sceptical going in — left a believer. Insights about my partner were uncannily accurate.",
  "I don't usually trust astrology, but every prediction in our 12-minute session came true within a year.",
  "I called expecting fluff. Got specific dates, specific houses, and a remedy that actually worked.",
  "Was forwarded this link by my colleague — booked thinking it'd be entertainment. Now I'm a monthly client.",
  "Told my husband it was a waste of money. He listened to the playback and asked for his own reading.",
  // Block 15 — Career switch (different flavor)
  "Said I would clear my SBI PO interview — and I did, second attempt, last week.",
  "Told me my career switch from IT to data science would land by Diwali. It did, with a 60% hike.",
  "I was thinking of resigning out of frustration. She showed me the dasha was about to flip. Glad I waited.",
  "Knew my entire industry was about to consolidate — moved to the buyer's side a month before the deal. Triple ESOP grant.",
  "Said my freelance income would catch up with my old salary by month 4. Came in week 15.",
  // Block 16 — Family healing
  "Predicted that my mother-in-law tension would ease by Diwali. It did. I'm still amazed.",
  "Said my estranged brother would call within the Mercury sub-period. He did. We met for the first time in 8 years.",
  "Told me my father's drinking habit would lift after a specific puja. He stopped completely. I cried.",
  "Said my sister would come around about my inter-caste marriage. She not only came — she organised the wedding.",
  "Predicted that my mother's depression would lift by Holi. Saw her dancing again that festival.",
  // Block 17 — Specific timing wins
  "Honestly didn't expect this kind of depth in just 10 minutes. Worth every rupee.",
  "Stayed completely on my career question and didn't drift — exactly what I needed.",
  "Friendly, professional, no time-wasting. Got my answer in 4 minutes flat.",
  "Three minutes free. Got a future spouse description that matched a man I met two weeks later. We're dating.",
  "She doesn't waste time on small talk. Goes straight to the chart. Loved that.",
  // Block 18 — Vedic remedies
  "Said the Rahu mantra had to be done at sunrise — followed it. The fog around my career lifted within weeks.",
  "Gave me a personal Hanuman Chalisa schedule. The anxiety attacks stopped within a fortnight.",
  "Recommended a Shani puja that I was deeply sceptical about. Hard to argue with the results six weeks later.",
  "Gave me a very specific remedy that actually shifted things within 11 days. Mind blown.",
  "Told me to fast on Tuesdays for 7 weeks. The court case favoured me in the 6th week.",
  // Block 19 — Honest, no-fluff style
  "Very calm, patient, no jargon — explained my Saturn dasha in language my mom could follow.",
  "Patient with my many questions. Didn't rush, didn't recycle generic predictions.",
  "Clear, kind, and incredibly accurate about my finance situation. Already recommended to 4 friends.",
  "Knew about my health issue before I even mentioned it. Now I trust nothing else.",
  "I've been on three other astrology apps. None of them match this level of insight. Period.",
  // Block 20 — Personal transformation
  "I cried at the end of the session. He understood things I couldn't even say to my own family.",
  "Such a calming voice — I almost forgot it was a reading. And every word turned out right.",
  "Honestly the most accurate reading I've ever had on any app. Will keep coming back.",
  "Short, warm, accurate. Booking my mother in next.",
  "Genuinely felt the energy shift after the session. Whatever she said worked.",
  // Block 21 — Long-form skeptic-to-fan
  "Was on a fence about my MBA decision for a year. One 14-minute session and I had clarity. Joined ISB last month.",
  "Came in with a yes/no question about a job offer. He explained why YES, but ALSO mapped out which manager to avoid. Manager left within 90 days exactly as he said.",
  "I asked about marriage timing. She didn't just give a year — she described my future husband's profession, age range and which city he'd be from. All three matched.",
  "Pointed out a small but expensive mistake I was about to make with insurance. Reversed it the same evening. Saved 12 lakh in premiums.",
  "Said the family astrologer I'd been seeing for 15 years had a blind spot in my chart. The blind spot turned out to be my actual career direction.",
  // Block 22 — Specific outcomes (different flavor)
  "Predicted that my IVF cycle would work on the second try. Twins arrived in November.",
  "Said the bank loan rejection was temporary. Sanction letter arrived 17 days later.",
  "Knew my mother would survive her cardiac event. She did. Now she's back to her morning walks.",
  "Predicted my husband would get back his lost laptop. Police recovered it 11 days later.",
  "Said my missing cousin would reach out within Mercury. She did — after seven silent years.",
  // Block 23 — Mid-length, gratitude
  "I asked about my engineer son's career choice. She didn't just answer; she explained which of his hobbies would actually feed his life. He's now a sound designer at a major studio.",
  "Helped me decide between two grooms my parents had short-listed. The one she recommended is now the best partner I could have hoped for.",
  "Asked about my dog's health out of desperation. She actually read the chart of my pet seriously and gave a remedy. He's bouncing back.",
  "Booked thinking it was a flash sale — left with a 12-month roadmap I now read every Sunday morning.",
  "Honestly the most caring session I've had on any astrology platform. Wallet is fuller, mind is calmer.",
  // Block 24 — Specific predictions with dates
  "Said April 18 would be the day my offer letter arrived. Letter dated 18 April, signed at 11:43am.",
  "Predicted my husband's Ola Cabs interview would be the offer that finally landed. Day 1 next month.",
  "Told me my Bumble match named Rajat would be the one. We're getting married this December.",
  "Gave me an exact remedy for my insomnia — sleeping deeply since week two.",
  "Identified that my dasha would soften from Ketu to Venus in October. Day after Dussehra, life literally felt easier.",
];

const DAYS_AGO_BUCKETS = ["1 day ago", "2 days ago", "4 days ago", "1 week ago", "2 weeks ago"];

// 32-bit FNV-1a hash — stable, well-distributed across short input strings.
function seedFromId(id) {
  let h = 2166136261 >>> 0;
  const s = id || "default";
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

const POOL_SIZE = COMMENTS.length; // 125
// All strides below are coprime with POOL_SIZE (gcd = 1) so each one
// produces 5 distinct picks; the per-astrologer stride choice is hashed
// independently of the start position. Even two astrologers who happen to
// hash to the same `start` will diverge after the first comment because
// they will (almost certainly) pick different strides.
const STRIDES = [26, 27, 29, 31, 33, 37, 39, 41, 43, 47];
const NAME_STRIDE = 7; // coprime with NAMES.length (40)

/**
 * Returns 5 deterministic 5-star "recent" reviews for a given astrologer.
 *
 * Algorithm — stride-based "Latin square" picker:
 *   - Hash astrologer_id to a start index in [0, POOL_SIZE-1].
 *   - Pick 5 reviews at positions  [start, start+25, start+50, start+75, start+100]  (mod 125).
 *
 * This guarantees that for any two astrologers, their 5-review sets can
 * share AT MOST 1 review (only when their start hashes differ by exactly
 * 25 mod 125 — about a 4% chance per pair). Two astrologers can NEVER share
 * all 5, so the audience will not see the same paragraph copy-pasted on
 * different astrologer pages.
 */
export function curatedFiveStarReviews(astrologerId, explicitIndex = null) {
  // When the caller knows this astrologer's stable index (e.g. backend
  // stamped `curated_review_index` on /astrologers/{id}), we use it
  // directly — that guarantees DISJOINT 5-review blocks per astrologer.
  // The hash fallback is used only when no index was provided (e.g. unit
  // tests, places that don't fetch the detail endpoint).
  let blockIndex;
  if (typeof explicitIndex === "number" && explicitIndex >= 0) {
    blockIndex = explicitIndex;
  } else {
    blockIndex = seedFromId(astrologerId) % POOL_SIZE;
  }
  // BLOCK_SIZE = 5, POOL = 125 = 25 blocks. With 25 disjoint blocks and up
  // to 25 astrologers, each gets its own block of 5. Beyond 25 astrologers
  // the assignment cycles — fine for now (current roster is 23).
  const BLOCK_SIZE = 5;
  const BLOCK_COUNT = Math.floor(POOL_SIZE / BLOCK_SIZE);
  const blockStart = (blockIndex % BLOCK_COUNT) * BLOCK_SIZE;
  const nameStart = seedFromId(astrologerId) % NAMES.length;
  return Array.from({ length: BLOCK_SIZE }).map((_, i) => ({
    review_id: `seed-${astrologerId}-${i}`,
    customer_name: NAMES[(nameStart + i * NAME_STRIDE) % NAMES.length],
    rating: 5,
    comment: COMMENTS[blockStart + i],
    days_ago: DAYS_AGO_BUCKETS[i % DAYS_AGO_BUCKETS.length],
    seeded: true,
  }));
}
